import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CheckIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { runnerApi, errorText, type BaselineResult, type Scan, type StatusGrid } from '../../../../api/migrationRunner';
import { Btn, ErrorBox, Pill, Seg, Spinner, when } from '../ui';
import { dbLabel, migName, plural, type CleanupCtx } from './shared';

const keyOfDate = (d: Date) => `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
const label = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
const folderKey = (folder: string) => {
  const m = /^migrat(?:e|ions)-(\d{4})-(\d{1,2})-(\d{1,2})/.exec(folder);
  return m ? Number(m[1]) * 10000 + Number(m[2]) * 100 + Number(m[3]) : null;
};
const cutKey = (cut: string) => {
  if (cut === 'all') return Infinity;
  const [y, m, d] = cut.split('-').map(Number);
  return y * 10000 + m * 100 + d;
};

/** Clean up › Start fresh (approved mockup): one checked baseline for everything up to a date. */
const FreshView: React.FC<{ ctx: CleanupCtx; pendingBefore: number | null }> = ({ ctx, pendingBefore }) => {
  const presets = useMemo(() => {
    const now = new Date();
    const prevMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
    const d14 = new Date(now.getTime() - 14 * 86400000);
    const d7 = new Date(now.getTime() - 7 * 86400000);
    return [
      { value: keyOfDate(prevMonthEnd), label: label(prevMonthEnd) },
      { value: keyOfDate(d14), label: label(d14) },
      { value: keyOfDate(d7), label: label(d7) },
      { value: 'all', label: 'Everything' },
    ];
  }, []);
  const [cutoff, setCutoff] = useState<string>(presets[0].value);
  const [custom, setCustom] = useState('');
  const [grid, setGrid] = useState<StatusGrid | null>(null);
  const [scan, setScan] = useState<Scan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [archiveDone, setArchiveDone] = useState(true);
  const [markDumps, setMarkDumps] = useState(true);
  const [hidden, setHidden] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<BaselineResult | null>(null);
  const [pendingAfter, setPendingAfter] = useState<number | null>(null);
  const before = useRef<number | null>(pendingBefore);
  const poll = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => { runnerApi.status().then(setGrid).catch(() => setGrid(null)); }, []);

  const follow = useCallback((s: Scan | null) => {
    setScan(s);
    if (poll.current) clearTimeout(poll.current);
    if (s?.status === 'running') {
      poll.current = setTimeout(async () => {
        try { follow(await runnerApi.scan(s.id)); } catch (err) { setError(errorText(err)); }
      }, 2000);
    }
  }, []);

  useEffect(() => {
    setResult(null);
    setError(null);
    setScan(null);
    let alive = true;
    runnerApi.latestScan('baseline', cutoff)
      .then((s) => {
        if (!alive) return;
        // A finished check older than 6 hours must be redone before it can be applied.
        if (s && s.status === 'done' && s.finishedAt && Date.now() - new Date(s.finishedAt).getTime() > 6 * 3600000) follow(null);
        else follow(s);
      })
      .catch(() => { /* not set up yet: the button says so */ });
    return () => { alive = false; if (poll.current) clearTimeout(poll.current); };
  }, [cutoff, follow]);

  const scope = useMemo(() => {
    if (!grid) return null;
    const limit = cutKey(cutoff);
    const migs = grid.migrations.filter((m) => !m.archived && (limit === Infinity || ((folderKey(m.folder) ?? Infinity) <= limit)));
    return { folders: new Set(migs.map((m) => m.folder)).size, databases: grid.targets.filter((t) => t.exists !== false).length };
  }, [grid, cutoff]);

  const start = async () => {
    setError(null);
    setResult(null);
    try {
      follow(await runnerApi.startScan('baseline', cutoff));
    } catch (err) {
      setError(errorText(err, 'Could not start the check'));
    }
  };

  const r = scan?.status === 'done' ? scan.result : null;
  const s = r?.summary;
  const checkLeft = s ? s.counts.check - s.checkZero : 0;
  const clear = s ? s.counts.inplace + s.checkZero + s.counts.obsolete + (markDumps ? r!.dumps.length : 0) : 0;
  const needed = (r?.items ?? []).filter((i) => i.verdict === 'needed');

  const apply = async () => {
    if (!scan) return;
    setBusy(true);
    setError(null);
    try {
      const res = await runnerApi.baseline({ scanId: scan.id, archiveDone, markDumps, hidden });
      setResult(res);
      ctx.changed();
      runnerApi.cleanupOverview().then((o) => setPendingAfter(o.counts.pending)).catch(() => {});
      const markId = res.markId;
      ctx.notify(`Baseline saved. ${plural(res.marked, 'pair')} marked${res.archivedFolders ? `, ${plural(res.archivedFolders, 'folder')} archived` : ''}.`, markId ? undo : undefined);
    } catch (err) {
      setError(errorText(err, 'Start fresh failed'));
    } finally {
      setBusy(false);
    }
  };

  async function undo() {
    if (!result?.markId) return;
    try {
      await runnerApi.undoMark(result.markId);
      setResult(null);
      setPendingAfter(null);
      ctx.notify('Baseline undone.');
      ctx.changed();
    } catch (err) {
      setError(errorText(err, 'Undo failed'));
    }
  }

  const byTarget = s ? Object.entries(s.byTarget).filter(([, v]) => v.inplace).map(([db, v]) => `${v.name || dbLabel(db)} ${v.inplace}`).join(' · ') : '';
  const running = scan?.status === 'running';

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-[15px] font-bold text-slate-900">Start fresh</h2>
        <p className="text-[12.5px] text-slate-500">For migrations you already applied by hand, with the old tool, or before the runner tracked them. Everything up to a date gets checked against every database, the registry included. What is really in place is marked done in one go. Only what's really missing is left.</p>
      </div>

      <div className="space-y-2.5 rounded-xl border border-slate-200 bg-white p-4">
        <b className="text-slate-900">1 · Clean up everything up to</b>
        <div className="flex flex-wrap items-center gap-2">
          <Seg<string> value={presets.some((p) => p.value === cutoff) ? cutoff : ''} options={presets} onChange={(v) => { setCustom(''); setCutoff(v); }} />
          <label className="flex items-center gap-1.5 text-[12.5px] text-slate-600">
            or
            <input
              type="date"
              value={custom}
              onChange={(e) => {
                setCustom(e.target.value);
                const [y, m, d] = e.target.value.split('-').map(Number);
                if (y && m && d) setCutoff(`${y}-${m}-${d}`);
              }}
              className="rounded-lg border border-slate-300 px-2 py-1 text-[12.5px]"
            />
          </label>
        </div>
        <p className="text-[12.5px] text-slate-500">Pick a date. Migrations after it are new work and stay as they are.</p>
        {!running && !r && (
          <Btn onClick={start} disabled={!ctx.ready || !ctx.isOwner}>
            <MagnifyingGlassIcon className="h-4 w-4" /> Check {scope ? `${plural(scope.folders, 'folder')} × ${plural(scope.databases, 'database')}` : 'every folder on every database'}
          </Btn>
        )}
        {running && (
          <div className="flex items-center gap-2 text-[12.5px] text-slate-500">
            <Spinner /> Dry-running {scan!.progress.done} of {scan!.progress.total}{scan!.progress.current ? ` (${scan!.progress.current})` : ''}…
          </div>
        )}
        {scan?.status === 'failed' && <ErrorBox>The check failed: {scan.error}</ErrorBox>}
      </div>

      {error && <ErrorBox>{error}</ErrorBox>}

      {r && s && (
        <>
          <section className="overflow-hidden rounded-xl border border-slate-200 bg-white">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-4 py-3">
              <b className="text-slate-900">2 · What the check found</b>
              <span className="flex items-center gap-2">
                <span className="text-[12px] text-slate-500">{when(scan!.finishedAt)}</span>
                <Pill tone="info">read-only, nothing changed</Pill>
                {!result && <Btn kind="ghost" small onClick={start} disabled={!ctx.isOwner}>Check again</Btn>}
              </span>
            </div>
            <ul className="divide-y divide-slate-100 text-[13px]">
              <Row title={`${s.counts.inplace.toLocaleString()} already in place`} sub={`${byTarget || 'None.'} Every statement would do nothing.`} pill={<Pill tone="ok">mark done</Pill>} />
              <Row title={`${s.checkZero.toLocaleString()} data fixes already done`} sub="UPDATE/DELETE only, and 0 rows still match them today." pill={<Pill tone="ok">mark not needed</Pill>} />
              <Row title={`${s.counts.obsolete.toLocaleString()} out of date`} sub="A later migration already replaced what they touch, so running them would fail." pill={<Pill tone="ok">mark not needed</Pill>} />
              <Row
                title={`${s.counts.needed.toLocaleString()} really missing`}
                sub={needed.length ? `e.g. ${needed.slice(0, 2).map((i) => `${migName(i.folderPath)} on ${i.companyName}`).join('; ')}. These stay pending.` : 'Nothing.'}
                pill={<Pill tone="warn">stay pending</Pill>}
              />
              <Row title={`${(s.counts.unknown + checkLeft).toLocaleString()} can't tell`} sub="Data fixes that still match rows, INSERTs, or statements the check can't read (procedures, triggers). These stay pending." pill={<Pill tone="warn">stay pending</Pill>} />
            </ul>
          </section>

          {!result && (
            <>
              <div className="space-y-2 rounded-xl border border-slate-200 bg-white p-4 text-[13px]">
                <b className="text-slate-900">3 · Tidy the folder list too (optional)</b>
                <label className="flex items-start gap-2"><input type="checkbox" className="mt-0.5" checked={archiveDone} onChange={(e) => setArchiveDone(e.target.checked)} /> Archive folders that are then done on every database. They leave the lists; History keeps them.</label>
                <label className="flex items-start gap-2"><input type="checkbox" className="mt-0.5" checked={markDumps} onChange={(e) => setMarkDumps(e.target.checked)} /> Mark the {plural(r.dumps.length, 'data-dump and copy file pair', 'data-dump and copy file pairs')} "never run" so they stop counting.</label>
                <label className="flex items-start gap-2"><input type="checkbox" className="mt-0.5" checked={hidden} onChange={(e) => setHidden(e.target.checked)} /> Hide this baseline from History (still in the audit log)</label>
              </div>
              <div className="flex justify-end">
                <Btn kind="success" onClick={apply} disabled={busy || !ctx.isOwner || (!clear && !archiveDone)}>
                  {busy ? 'Saving…' : `Clear ${clear.toLocaleString()}${archiveDone ? ' · archive done folders' : ''}`}
                </Btn>
              </div>
            </>
          )}

          {result && (
            <div className="flex flex-wrap items-center gap-3 rounded-xl bg-green-50 px-4 py-3 text-[13px] text-slate-700">
              <CheckIcon className="h-4 w-4 shrink-0 text-green-700" />
              <span className="min-w-0 flex-1">
                <b>Done.</b> {plural(result.marked, 'pair')} marked{result.archivedFolders ? `, ${plural(result.archivedFolders, 'folder')} archived` : ''}.
                {before.current !== null && pendingAfter !== null && <> Pending went from {before.current.toLocaleString()} to {pendingAfter.toLocaleString()}.</>}
                {' '}Each one is saved as "Baseline {label(new Date())}" with your name. Undo the whole baseline here or from History for 30 days.
              </span>
              {result.markId && <Btn kind="ghost" small onClick={undo}>Undo</Btn>}
              <Btn kind="ghost" small onClick={() => ctx.go('pending')}>See what's left</Btn>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const Row: React.FC<{ title: string; sub: string; pill: React.ReactNode }> = ({ title, sub, pill }) => (
  <li className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
    <div className="min-w-0 flex-1"><b className="text-slate-900">{title}</b><div className="text-[12.5px] text-slate-500">{sub}</div></div>
    {pill}
  </li>
);

export default FreshView;
